/**
 * Signal Banner — the traffic light indicator card showing green/amber/red.
 */

import { Badge } from "@/ui/primitives/badge";
import { cn } from "@/utils/misc";
import { CheckCircle2, Gauge, Loader2, XCircle } from "lucide-react";

const SIGNAL_CFG = {
  green: {
    label: "Green — No Indicators of Complexity",
    bg: "bg-emerald-50/80 dark:bg-emerald-500/10",
    border: "border-emerald-200 dark:border-emerald-500/25",
    titleColor: "text-emerald-900 dark:text-emerald-300",
    textColor: "text-emerald-700/80 dark:text-emerald-400/90",
    icon: CheckCircle2,
    iconColor: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-100 dark:bg-emerald-500/15",
  },
  amber: {
    label: "Amber — Further Investigation Recommended",
    bg: "bg-amber-50/80 dark:bg-amber-500/10",
    border: "border-amber-200 dark:border-amber-500/25",
    titleColor: "text-amber-900 dark:text-amber-100",
    textColor: "text-amber-700/80 dark:text-amber-400/80",
    icon: Gauge,
    iconColor: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
  },
  red: {
    label: "Red — Further Investigation Recommended",
    bg: "bg-red-50/80 dark:bg-red-500/10",
    border: "border-red-200 dark:border-red-500/25",
    titleColor: "text-red-900 dark:text-red-300",
    textColor: "text-red-700/80 dark:text-red-400/90",
    icon: XCircle,
    iconColor: "text-red-600 dark:text-red-400",
    iconBg: "bg-red-100 dark:bg-red-500/15",
  },
} as const;

interface SignalBannerProps {
  signal?: "green" | "amber" | "red";
  criteriaMet: number;
  recommendation?: string;
  isRunning: boolean;
}

export function SignalBanner({
  signal,
  criteriaMet,
  recommendation,
  isRunning,
}: SignalBannerProps) {
  if (isRunning) {
    return (
      <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-5 dark:border-blue-500/25 dark:bg-blue-500/10">
        <div className="flex items-center gap-4">
          <div className="shrink-0 rounded-full bg-blue-100 p-3 dark:bg-blue-500/15">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-300">
              Assessing job complexity
            </p>
            <p className="mt-0.5 text-xs text-blue-700/70 dark:text-blue-400/90">
              Analysing corporate documents, director disclosures, and asset
              records...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!signal) return null;

  const cfg = SIGNAL_CFG[signal];
  const Icon = cfg.icon;

  return (
    <div className={cn("rounded-xl border p-5", cfg.bg, cfg.border)}>
      <div className="flex items-start gap-4">
        <div
          className={cn(
            "shrink-0 rounded-lg border p-2.5",
            cfg.iconBg,
            cfg.border,
          )}
        >
          <Icon className={cn("h-6 w-6", cfg.iconColor)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <h2 className={cn("text-base font-semibold", cfg.titleColor)}>
              {cfg.label}
            </h2>
            <Badge variant="outline" size="sm" className="tabular-nums">
              {criteriaMet}/3 criteria met
            </Badge>
          </div>
          {recommendation && (
            <p className={cn("mt-1.5 text-sm leading-relaxed", cfg.textColor)}>
              {recommendation}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function SignalDot({ signal }: { signal: string }) {
  const color: Record<string, string> = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
  };
  return (
    <span
      className={cn(
        "ml-1.5 inline-flex h-2.5 w-2.5 rounded-full",
        color[signal] ?? "bg-gray-400",
      )}
    />
  );
}
